import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Tag, Plus, Sparkles, Trash2, FileUp, Loader2 } from "lucide-react";
import GenerateArticleDialog from "./GenerateArticleDialog";
import CountryPickerDialog, { flagFor } from "./CountryPickerDialog";

interface QueueRow {
  id: string;
  keyword: string;
  created_at: string;
  country_code?: string | null;
}

interface Props {
  onGenerated: (articleId: string) => void;
}

/**
 * Tableau central des mots-clés du Guide.
 * - Saisie libre (texte / .txt / liste séparée par , ; ou retour ligne)
 * - Persistance dans blog_keyword_queue (partagée avec l'onglet Recherche)
 * - Compteur d'usages calculé sur blog_articles (title / slug / keywords)
 * - Tri non-utilisés en haut, filtre pour masquer les exploités
 * - Génération directe d'un article par mot-clé
 */
const KeywordTable = ({ onGenerated }: Props) => {
  const [rows, setRows] = useState<QueueRow[]>([]);
  const [articles, setArticles] = useState<{ title: string; slug: string; keywords: string[] | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [adding, setAdding] = useState(false);
  const [hideUsed, setHideUsed] = useState(false);
  const [dialogKw, setDialogKw] = useState<string | null>(null);
  const [pendingList, setPendingList] = useState<string[] | null>(null);
  const [countryOpen, setCountryOpen] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const load = async () => {
    const [{ data: q }, { data: a }] = await Promise.all([
      (supabase as any)
        .from("blog_keyword_queue")
        .select("id, keyword, created_at, country_code")
        .order("created_at", { ascending: false }),
      (supabase as any)
        .from("blog_articles")
        .select("title, slug, keywords"),
    ]);
    setRows(q || []);
    setArticles(a || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const onChange = () => load();
    window.addEventListener("blog-keyword-queue-changed", onChange);
    return () => window.removeEventListener("blog-keyword-queue-changed", onChange);
  }, []);

  const parseInput = (raw: string): string[] => {
    return raw
      .split(/[\n,;]+/g)
      .map((s) => s.trim())
      .filter((s) => s.length > 0 && s.length <= 120);
  };

  const addKeywords = async (list: string[], countryCode: string) => {
    const unique = Array.from(new Set(list.map((s) => s.toLowerCase()))).map((low) =>
      list.find((s) => s.toLowerCase() === low) as string
    );
    if (unique.length === 0) {
      toast.error("Aucun mot-clé valide détecté.");
      return;
    }
    setAdding(true);
    let inserted = 0;
    let skipped = 0;
    for (const kw of unique) {
      const { error } = await (supabase as any)
        .from("blog_keyword_queue")
        .insert({ keyword: kw, status: "pending", country_code: countryCode === "INT" ? null : countryCode });
      if (!error) inserted++;
      else if (/duplicate|unique/i.test(error.message)) skipped++;
      else {
        toast.error(error.message);
        break;
      }
    }
    setAdding(false);
    setInput("");
    if (inserted > 0) toast.success(`${inserted} mot${inserted > 1 ? "s" : ""}-clé${inserted > 1 ? "s" : ""} ajouté${inserted > 1 ? "s" : ""}${skipped > 0 ? ` (${skipped} déjà présent${skipped > 1 ? "s" : ""})` : ""}`);
    else if (skipped > 0) toast.info(`${skipped} mot-clé(s) déjà présent(s).`);
    window.dispatchEvent(new CustomEvent("blog-keyword-queue-changed"));
    load();
  };

  const askCountryThen = (list: string[]) => {
    if (list.length === 0) { toast.error("Aucun mot-clé valide détecté."); return; }
    setPendingList(list);
    setCountryOpen(true);
  };
  const handleAdd = () => askCountryThen(parseInput(input));

  const handleFile = async (file: File) => {
    if (file.size > 1024 * 1024) {
      toast.error("Fichier trop volumineux (max 1 Mo).");
      return;
    }
    const text = await file.text();
    askCountryThen(parseInput(text));
  };

  const removeRow = async (id: string) => {
    const { error } = await (supabase as any).from("blog_keyword_queue").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setRows((s) => s.filter((r) => r.id !== id));
  };

  // Compteur d'usages : présence dans title, slug ou keywords[]
  const usageMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of rows) {
      const low = r.keyword.toLowerCase();
      let count = 0;
      for (const a of articles) {
        const t = (a.title || "").toLowerCase();
        const s = (a.slug || "").toLowerCase();
        const kws = (a.keywords || []).map((k) => (k || "").toLowerCase());
        if (t.includes(low) || s.includes(low.replace(/\s+/g, "-")) || kws.includes(low)) {
          count++;
        }
      }
      map.set(r.id, count);
    }
    return map;
  }, [rows, articles]);

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    arr.sort((a, b) => {
      const ua = usageMap.get(a.id) || 0;
      const ub = usageMap.get(b.id) || 0;
      if (ua !== ub) return ua - ub;
      return a.keyword.localeCompare(b.keyword);
    });
    return hideUsed ? arr.filter((r) => (usageMap.get(r.id) || 0) === 0) : arr;
  }, [rows, usageMap, hideUsed]);

  const totalUnused = rows.filter((r) => (usageMap.get(r.id) || 0) === 0).length;

  const handleGenerated = async (articleId: string, sourceKw: string) => {
    // Retire le mot-clé de la file après génération
    const match = rows.find((r) => r.keyword.toLowerCase() === sourceKw.toLowerCase());
    if (match) await (supabase as any).from("blog_keyword_queue").delete().eq("id", match.id);
    setDialogKw(null);
    window.dispatchEvent(new CustomEvent("blog-keyword-queue-changed"));
    onGenerated(articleId);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Tag className="h-5 w-5 text-primary" />
            Tableau des mots-clés du Guide
            <Badge variant="secondary" className="ml-2">{rows.length} total</Badge>
            <Badge variant="outline" className="ml-1">{totalUnused} non utilisé{totalUnused > 1 ? "s" : ""}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Textarea
              rows={3}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Collez ici une liste de mots-clés (séparés par virgule, point-virgule ou retour ligne)&#10;Ex : marrakech excursion, riad essaouira, transfert agadir"
              maxLength={5000}
            />
            <div className="flex flex-wrap gap-2 items-center">
              <Button onClick={handleAdd} disabled={adding || !input.trim()} className="bg-gradient-gold text-primary-foreground">
                {adding ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Ajouter au tableau
              </Button>
              <input
                ref={fileRef}
                type="file"
                accept=".txt,text/plain"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFile(f);
                  e.target.value = "";
                }}
              />
              <Button variant="outline" onClick={() => fileRef.current?.click()}>
                <FileUp className="mr-2 h-4 w-4" /> Importer un .txt
              </Button>
              <div className="ml-auto flex items-center gap-2 text-sm">
                <Switch checked={hideUsed} onCheckedChange={setHideUsed} id="hide-used" />
                <label htmlFor="hide-used" className="cursor-pointer text-muted-foreground">
                  Masquer les mots-clés déjà exploités
                </label>
              </div>
            </div>
          </div>

          <div className="rounded-md border border-border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Mot-clé</TableHead>
                  <TableHead className="w-[120px]">Pays</TableHead>
                  <TableHead className="w-[140px]">Usages</TableHead>
                  <TableHead className="w-[220px] text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Chargement…</TableCell></TableRow>
                ) : sortedRows.length === 0 ? (
                  <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6 italic">
                    {rows.length === 0 ? "Aucun mot-clé pour l'instant. Collez-en ci-dessus ou confirmez-en depuis l'onglet Recherche." : "Aucun mot-clé non exploité — décochez le filtre pour tout voir."}
                  </TableCell></TableRow>
                ) : sortedRows.map((r) => {
                  const usage = usageMap.get(r.id) || 0;
                  return (
                    <TableRow key={r.id}>
                      <TableCell className="font-medium">{r.keyword}</TableCell>
                      <TableCell>
                        <span className="text-lg" title={r.country_code || "International"}>{flagFor(r.country_code)}</span>
                        <span className="ml-1 text-[10px] text-muted-foreground">{r.country_code || "INT"}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant={usage === 0 ? "default" : "secondary"} className={usage === 0 ? "bg-emerald-600 text-white" : ""}>
                          {usage === 0 ? "Non utilisé" : `${usage} article${usage > 1 ? "s" : ""}`}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button size="sm" onClick={() => setDialogKw(r.keyword)} className="bg-primary text-primary-foreground">
                            <Sparkles className="h-4 w-4 mr-1" /> Générer
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => removeRow(r.id)} className="text-destructive">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <CountryPickerDialog
        open={countryOpen}
        onOpenChange={setCountryOpen}
        onConfirm={(code) => {
          if (pendingList) { addKeywords(pendingList, code); setPendingList(null); }
        }}
      />

      {dialogKw && (
        <GenerateArticleDialog
          open={!!dialogKw}
          onOpenChange={(o) => { if (!o) setDialogKw(null); }}
          keywords={[dialogKw]}
          targetCountry={(rows.find((r) => r.keyword === dialogKw)?.country_code) || null}
          onRemoveKeyword={() => setDialogKw(null)}
          onGenerated={(id) => handleGenerated(id, dialogKw)}
        />
      )}
    </>
  );
};

export default KeywordTable;